import React, { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Slider from 'rc-slider';

import { COEFFICIENT, COEFFICIENT_KEY } from '../../../../constants/formFields';
import { COEFFICIENTS_SLIDER } from '../../../../constants/materialAssessment';
import { realToSliderValueCoefficient } from '../../../../utils/helpers';

import type IInsightsDashboardSlider from '../interfaces/IInsightsDashboardSlider';

import 'rc-slider/assets/index.css';

import styles from '../scss/MAInsightsDashboardSlider.module.scss';

export default function InsightDashboardSlider({
  control,
  coefficientData,
  fields,
  onSliderChange,
}: IInsightsDashboardSlider) {
  const { t } = useTranslation();

  const { MIN, MAX, STEP } = COEFFICIENTS_SLIDER;

  const marks = useMemo(() => {
    const obj: Record<number, React.ReactNode> = {};
    for (let v = MIN; v <= MAX; v += STEP) {
      obj[v] = (
        <span className={styles.mark}>
          {v}
        </span>
      );
    }
    return obj;
  }, [MIN, MAX, STEP]);

  return (
    <div className={styles.weightsContainer} role="table" aria-label="Stakeholder weighting">
      <div className={styles.thead} role="row">
        <div className={styles.th} role="columnheader">{t('materialityAssessment.typeStakeholder')}</div>
        <div className={styles.th} role="columnheader">{t('materialityAssessment.weighting')}</div>
      </div>

      {fields.map((field, index) => {
        const meta = coefficientData.find((x) => x.id === field.id);
        const label = meta?.respondentType
          ? t(`materialityAssessment.${meta.respondentType}`)
          : String(field.id);

        return (
          <div key={field[COEFFICIENT_KEY]} className={styles.tr} role="row">
            <div className={styles.tdLabel} role="cell">
              {label}
            </div>

            <div className={styles.tdSlider} role="cell">
              <Controller
                control={control}
                name={`${COEFFICIENT}.${index}.impact`}
                render={({ field: sliderField }) => (
                  <Slider
                    className={styles.slider}
                    min={MIN}
                    max={MAX}
                    step={STEP}
                    value={realToSliderValueCoefficient(sliderField.value)}
                    marks={marks}
                    dots
                    aria-label={`${meta?.respondentType ?? field.id} weighting`}
                    onChange={(val: any) => {
                      if (onSliderChange) {
                        onSliderChange(index, val, field, sliderField);
                      }
                    }}
                  />
                )}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
